import React, { useState, CSSProperties, useEffect } from "react";
import {
  GoogleMap,
  MarkerClusterer,
  Marker,
  OverlayView,
} from "@react-google-maps/api";
import withLoadedMap from "HoC/withLoadedMap";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

import clusterUrl from "../../../images/cluster.png";
import markerUrl from "../../../images/marker_red.png";
import { INewsDisplay } from "../../../api/types/NewsEntity";
import EventMapCard from "../Cards/EventMapCard";

interface GoogleMapProps {
  markers: INewsDisplay[];
  freeZoom?: boolean;
  getUserLocation?: boolean;
  defaultZoom?: number;
}
const clusterStyles = [
  {
    url: clusterUrl,
    width: 45,
    height: 45,
    textColor: "#fff",
  },
];
const markerIcon = {
  url: markerUrl,
};
const mapContainerStyle: CSSProperties = {
  flex: 1,
  height: "600px",
  width: "calc(100vw - 4em)",
  position: "relative",
  left: "calc(-50vw + 50%)",
  margin: "0 2em 2em",
  marginTop: "1.5em",
};

const ClusterMap: React.FC<GoogleMapProps> = ({
  markers,
  freeZoom = false,
  defaultZoom = 3,
  getUserLocation = false,
}) => {
  const { t } = useTranslation();
  const [mapCenter, setMapCenter] = useState({
    lat: 56.94525146484375,
    lng: 24.072887420654297,
  });

  useEffect(() => {
    if (getUserLocation) {
      if (navigator.permissions && navigator.permissions.query) {
        navigator.permissions.query({ name: "geolocation" }).then((result) => {
          if (result.state === "granted" || result.state === "prompt") {
            navigator.geolocation.getCurrentPosition(
              async (success) => {
                setMapCenter({
                  lat: success.coords.latitude,
                  lng: success.coords.longitude,
                });
              },
              (geoError) => {
                console.error("Unexpected Geocoder Error", geoError);
                toast.error(t("Toasts.LocationDenied"));
              },
            );
          } else if (result.state === "denied") {
            toast.error(t("Toasts.LocationDenied"));
          }
        });
      }
    }
  }, []);

  const [selectedMarker, setSelectedMarker] = useState<INewsDisplay>();

  const mapClick = () => {
    setSelectedMarker(undefined);
  };

  const handleMarkerClick = (event: INewsDisplay) => {
    setSelectedMarker(event);
    if (event.address) {
      setMapCenter({
        lat: Number(event.address.geolocation.lat),
        lng: Number(event.address.geolocation.lng),
      });
    }
  };

  return (
    <GoogleMap
      mapContainerStyle={mapContainerStyle}
      center={mapCenter}
      zoom={defaultZoom}
      onClick={mapClick}
      options={{
        ...(freeZoom
          ? { gestureHandling: "greedy" }
          : { gestureHandling: "cooperative" }),
      }}
    >
      <MarkerClusterer
        enableRetinaIcons
        averageCenter
        styles={clusterStyles}
        options={{ maxZoom: 15 }}
      >
        {(clusterer) =>
          markers &&
          markers.map((marker: INewsDisplay, idx: number) => {
            if (
              marker.address?.geolocation?.lat &&
              marker.address?.geolocation?.lng
            ) {
              return (
                <Marker
                  key={`${marker.title}-${idx + 1}`}
                  position={{
                    lat: Number(marker.address.geolocation.lat),
                    lng: Number(marker.address.geolocation.lng),
                  }}
                  onClick={() => handleMarkerClick(marker)}
                  icon={markerIcon}
                  clusterer={clusterer}
                />
              );
            }
            return null;
          })
        }
      </MarkerClusterer>
      {selectedMarker && (
        <OverlayView
          position={mapCenter}
          mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
        >
          <EventMapCard event={selectedMarker} route={selectedMarker.type} />
        </OverlayView>
      )}
    </GoogleMap>
  );
};

export default withLoadedMap(ClusterMap);
